import axios from 'axios';
import i18next from 'i18next';

import generateTraceParent from '@/utils/generateTraceParent';

const axiosInstance = axios.create({
  baseURL: '/',
});

const liveServerAxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_CAMERAFI_API_URL,
});

const gateWayAxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_CAMERAFI_API_GATEWAY_URL,
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers['traceparent'] = generateTraceParent();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

const postDeletionToken = async (idToken: string) =>
  await axiosInstance.post<{
    token: string;
  }>('/api/token-for-delete', null, {
    headers: {
      Authorization: 'Bearer ' + idToken,
    },
  });

const getDeletionToken = async (idToken: string) =>
  await axiosInstance.get<{
    token: string;
  }>('/api/token-for-delete', {
    headers: {
      Authorization: 'Bearer ' + idToken,
    },
  });

const deleteUser = async ({
  uid,
  refreshToken,
  tokenForDelete,
}: {
  uid: string;
  refreshToken?: string;
  tokenForDelete: string;
}) =>
  await axiosInstance.delete('/api/users', {
    params: {
      uid,
      refreshToken,
    },
    headers: {
      Authorization: `Bearer ${tokenForDelete}`,
    },
  });

const getUser = async (idToken: string) =>
  await axiosInstance.get<{
    uid: string;
    userName: string;
  }>('/api/user', {
    headers: {
      Authorization: 'Bearer ' + idToken,
    },
  });

const createCustomToken = async ({
  idToken,
  params,
}: {
  idToken: string;
  params: {
    lastVisitedService?: string;
    isNewUser?: string;
    gaClientId?: string;
  };
}) =>
  await axiosInstance.get<{
    token: string;
  }>('/api/login', {
    headers: {
      Authorization: 'Bearer ' + idToken,
    },
    params,
  });

const updateUserName = async ({
  idToken,
  nickname,
}: {
  idToken: string;
  nickname: string;
}) =>
  await axiosInstance.get('/api/update-username', {
    headers: {
      Authorization: 'Bearer ' + idToken,
    },
    params: {
      username: nickname,
    },
  });

const verifyEmail = async (idToken: string) =>
  await axiosInstance.get('/api/verify-email', {
    headers: {
      Authorization: 'Bearer ' + idToken,
    },
    params: {
      language: i18next.t('languageParameter'),
    },
  });

const sendResetPasswordEmail = async (email: string) =>
  await axiosInstance.get('/api/reset-password', {
    params: {
      email: email,
    },
  });

const getUserInfoByUid = async ({
  uid,
  token,
}: {
  uid: string;
  token: string;
}) =>
  await liveServerAxiosInstance.get<{ nickname: string }>(
    `/api/v1/users/${uid}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

const getUserInfo = async ({
  uid,
  idToken,
}: {
  uid: string;
  idToken: string;
}) =>
  await gateWayAxiosInstance.get<{
    privacy?: {
      birthday: string;
      gender: string;
    };
    channel: {
      category?: string;
    };
  }>(`/studio/api/v1/users/${uid}/studio`, {
    headers: {
      Authorization: 'Bearer ' + idToken,
    },
  });

const getStudioUserInfo = async ({
  uid,
  idToken,
}: {
  uid: string;
  idToken: string;
}) =>
  await gateWayAxiosInstance.get(`/studio/api/v1/users/${uid}/studio`, {
    headers: {
      Authorization: 'Bearer ' + idToken,
    },
  });

const getMembershipList = async (channelId: number) =>
  await gateWayAxiosInstance.get(`/studio/public-api/v1/membership/plans`, {
    params: {
      channelId,
    },
  });

export {
  createCustomToken,
  deleteUser,
  getUser,
  postDeletionToken,
  getDeletionToken,
  updateUserName,
  verifyEmail,
  getUserInfoByUid,
  getUserInfo,
  getMembershipList,
  getStudioUserInfo,
  sendResetPasswordEmail,
};
